import React from 'react';
import type {
  AgeLevel, ExperienceLevel, Sport,
  FootballOffensiveScheme, FootballDefensiveScheme,
  BasketballOffensiveScheme, BasketballDefensiveScheme,
  SoccerFormation, SoccerStyle,
  BaseballFocus,
  HockeySystem,
  VolleyballOffense, VolleyballDefense,
  LacrosseOffense, LacrosseDefense,
  TennisStyle
} from '../../types';
import { AGE_LEVELS, getAgeLevelLabel } from '../../types';
import { SportFormats } from '../../types';
import {
  FOOTBALL_OFFENSIVE_FORMATIONS,
  FOOTBALL_DEFENSIVE_FORMATIONS,
  SOCCER_FORMATIONS
} from '../../data/sportsFormations';
import { MobileSelect } from '../ui/MobileSelect';

interface SettingsFormState {
  name: string;
  sport: Sport;
  ageLevel: AgeLevel;
  experienceLevel: ExperienceLevel;
  rosterSize: number;
  format: string;
  offensiveScheme?: string;
  defensiveScheme?: string;
  innings?: number; // For baseball/softball
}

interface TeamInformationSectionProps {
  formState: SettingsFormState;
  onInputChange: (field: keyof SettingsFormState, value: string | number) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const TeamInformationSection: React.FC<TeamInformationSectionProps> = ({
  formState,
  onInputChange,
  onSubmit
}) => {
  const experienceLevels: ExperienceLevel[] = ['Beginner', 'Intermediate', 'Advanced'];

  const footballOffensiveSchemes = FOOTBALL_OFFENSIVE_FORMATIONS.map(f => f.name).sort();
  const footballDefensiveSchemes = FOOTBALL_DEFENSIVE_FORMATIONS.map(f => f.name).sort();

  const basketballOffensiveSchemes: BasketballOffensiveScheme[] = [
    'Motion', 'Princeton', 'Triangle', 'Pick and Roll', 'Fast Break', 'Flex', 'Zone'
  ];
  const basketballDefensiveSchemes: BasketballDefensiveScheme[] = [
    'Man-to-Man', '2-3 Zone', '3-2 Zone', '1-3-1 Zone', 'Box and One', 'Full Court Press', 'Half Court Press'
  ];

  const soccerFormations = SOCCER_FORMATIONS.map(f => f.name).sort();
  const soccerStyles: SoccerStyle[] = [
    'Possession', 'Counter-Attack', 'High Press', 'Tiki-Taka', 'Direct', 'Total Football', 'Park the Bus'
  ];

  const baseballFocuses: BaseballFocus[] = [
    'Small Ball', 'Power Hitting', 'Defensive', 'Balanced', 'Pitching Focus', 'Speed Focus'
  ];

  const hockeySystems: HockeySystem[] = [
    'Dump and Chase', 'Neutral Zone Trap', 'Left Wing Lock', '1-2-2 Forecheck', '2-1-2 Forecheck'
  ];

  const volleyballOffenses: VolleyballOffense[] = ['5-1', '6-2', '4-2', 'International', 'West Coast'];
  const volleyballDefenses: VolleyballDefense[] = ['Rotation', 'Perimeter', 'Man-Up', '6-Up', 'Base'];

  const lacrosseOffenses: LacrosseOffense[] = ['Motion', '1-4-1', '2-3-1', '3-3', 'Wheel'];
  const lacrosseDefenses: LacrosseDefense[] = ['Man-to-Man', 'Zone', 'Backer', 'Slide Package', 'Crease Defense'];

  const tennisStyles: TennisStyle[] = ['Baseline', 'Serve-and-Volley', 'All-Court', 'Counterpuncher', 'Aggressive Baseline'];

  const toOpts = (arr: string[], placeholder: string) => [
    { value: '', label: placeholder },
    ...arr.map((v) => ({ value: v, label: v })),
  ];

  const renderSportSpecificFields = () => {
    switch (formState.sport) {
      case 'Football':
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(footballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(footballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );

      case 'FlagFootball':
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(footballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(footballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );

      case 'Basketball':
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(basketballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(basketballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );

      case 'Soccer':
        return (
          <>
            <div>
              <label className="form-label">Formation</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(soccerFormations, 'Select Formation')}
                aria-label="Formation"
                sheetTitle="Formation"
              />
            </div>
            <div>
              <label className="form-label">Playing Style</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(soccerStyles, 'Select Playing Style')}
                aria-label="Playing Style"
                sheetTitle="Playing Style"
              />
            </div>
          </>
        );

      case 'Baseball':
      case 'Softball':
        return (
          <>
            <div>
              <label className="form-label">Team Focus</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(baseballFocuses, 'Select Team Focus')}
                aria-label="Team Focus"
                sheetTitle="Team Focus"
              />
            </div>
            <div>
              <label className="form-label">Number of Innings</label>
              <MobileSelect
                className="select"
                value={String(formState.innings || 7)}
                onChange={(v) => onInputChange('innings', parseInt(v))}
                options={[
                  { value: '4', label: '4 Innings (T-Ball)' },
                  { value: '5', label: '5 Innings (Coach Pitch)' },
                  { value: '6', label: '6 Innings (Youth)' },
                  { value: '7', label: '7 Innings (Standard)' },
                  { value: '9', label: '9 Innings (High School/Adult)' },
                ]}
                aria-label="Number of Innings"
                sheetTitle="Number of Innings"
              />
              <p className="text-xs text-gray-600 mt-1">
                Select the number of innings your league plays. This affects depth chart randomization.
              </p>
            </div>
          </>
        );

      case 'Hockey':
        return (
          <div>
            <label className="form-label">Team System</label>
            <MobileSelect
              className="select"
              value={formState.offensiveScheme || ''}
              onChange={(v) => onInputChange('offensiveScheme', v)}
              options={toOpts(hockeySystems, 'Select Team System')}
              aria-label="Team System"
              sheetTitle="Team System"
            />
          </div>
        );

      case 'Volleyball':
        return (
          <>
            <div>
              <label className="form-label">Offensive System</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(volleyballOffenses, 'Select Offensive System')}
                aria-label="Offensive System"
                sheetTitle="Offensive System"
              />
            </div>
            <div>
              <label className="form-label">Defensive System</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(volleyballDefenses, 'Select Defensive System')}
                aria-label="Defensive System"
                sheetTitle="Defensive System"
              />
            </div>
          </>
        );

      case 'Lacrosse':
        return (
          <>
            <div>
              <label className="form-label">Offensive System</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(lacrosseOffenses, 'Select Offensive System')}
                aria-label="Offensive System"
                sheetTitle="Offensive System"
              />
            </div>
            <div>
              <label className="form-label">Defensive System</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(lacrosseDefenses, 'Select Defensive System')}
                aria-label="Defensive System"
                sheetTitle="Defensive System"
              />
            </div>
          </>
        );

      case 'Tennis':
        return (
          <div>
            <label className="form-label">Playing Style</label>
            <MobileSelect
              className="select"
              value={formState.offensiveScheme || ''}
              onChange={(v) => onInputChange('offensiveScheme', v)}
              options={toOpts(tennisStyles, 'Select Playing Style')}
              aria-label="Playing Style"
              sheetTitle="Playing Style"
            />
          </div>
        );

      default:
        return (
          <>
            <div>
              <label className="form-label">Offensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(v) => onInputChange('offensiveScheme', v)}
                options={toOpts(footballOffensiveSchemes, 'Select Offensive Scheme')}
                aria-label="Offensive Scheme"
                sheetTitle="Offensive Scheme"
              />
            </div>
            <div>
              <label className="form-label">Defensive Scheme</label>
              <MobileSelect
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(v) => onInputChange('defensiveScheme', v)}
                options={toOpts(footballDefensiveSchemes, 'Select Defensive Scheme')}
                aria-label="Defensive Scheme"
                sheetTitle="Defensive Scheme"
              />
            </div>
          </>
        );
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="form-label">Team Name</label>
            <input
              type="text"
              className="input"
              value={formState.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="form-label">Sport</label>
            <div className="w-full px-3 py-2 border border-stone bg-cream rounded-xl text-dark font-medium">
              {formState.sport}
            </div>
          </div>

          <div>
            <label className="form-label">Format</label>
            <MobileSelect
              className="select"
              value={formState.format}
              onChange={(v) => onInputChange('format', v)}
              options={[
                { value: '', label: 'Select Format' },
                ...SportFormats[formState.sport].map((f) => ({ value: f, label: f })),
              ]}
              aria-label="Format"
              sheetTitle="Format"
            />
          </div>

          <div>
            <label className="form-label">Roster Size</label>
            <input
              type="number"
              min="1"
              className="input"
              value={formState.rosterSize}
              onChange={(e) => onInputChange('rosterSize', parseInt(e.target.value))}
              required
            />
          </div>

          <div>
            <label className="form-label">Age Level</label>
            <MobileSelect
              className="select"
              value={formState.ageLevel}
              onChange={(v) => onInputChange('ageLevel', v as AgeLevel)}
              options={[
                { value: '', label: 'Select Age Level' },
                ...AGE_LEVELS.map((l) => ({ value: l, label: getAgeLevelLabel(l) })),
              ]}
              aria-label="Age Level"
              sheetTitle="Age Level"
            />
          </div>

          <div>
            <label className="form-label">Experience Level</label>
            <MobileSelect
              className="select"
              value={formState.experienceLevel}
              onChange={(v) => onInputChange('experienceLevel', v as ExperienceLevel)}
              options={[
                { value: '', label: 'Select Experience Level' },
                ...experienceLevels.map((l) => ({ value: l, label: l })),
              ]}
              aria-label="Experience Level"
              sheetTitle="Experience Level"
            />
          </div>

          {renderSportSpecificFields()}
        </div>
      </form>
    </>
  );
};
